import { useEffect, useState } from 'react';
import { MoreHorizontal, Plus, Search, Code2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { adminProblemsApi, type AdminProblem } from '@/lib/api/admin-problems';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { PRIMARY_ACTION } from './actionStyles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const TOPICS = [
  { label: 'Array', slug: 'array' },
  { label: 'String', slug: 'string' },
  { label: 'Hash Table', slug: 'hash-table' },
  { label: 'Dynamic Programming', slug: 'dynamic-programming' },
  { label: 'Math', slug: 'math' },
  { label: 'Sorting', slug: 'sorting' },
  { label: 'Greedy', slug: 'greedy' },
  { label: 'Binary Search', slug: 'binary-search' },
  { label: 'DFS', slug: 'depth-first-search' },
  { label: 'BFS', slug: 'breadth-first-search' },
  { label: 'Bit Manipulation', slug: 'bit-manipulation' },
  { label: 'Matrix', slug: 'matrix' },
  { label: 'Two Pointers', slug: 'two-pointers' },
  { label: 'Tree', slug: 'tree' },
  { label: 'Binary Tree', slug: 'binary-tree' },
  { label: 'Heap', slug: 'heap-priority-queue' },
  { label: 'Stack', slug: 'stack' },
  { label: 'Graph', slug: 'graph' },
  { label: 'Sliding Window', slug: 'sliding-window' },
  { label: 'Backtracking', slug: 'backtracking' },
  { label: 'Linked List', slug: 'linked-list' },
  { label: 'Database', slug: 'database' },
  { label: 'Design', slug: 'design' },
];

const STATUS_COLORS: Record<string, string> = {
  published: 'border-emerald-500 text-emerald-600 bg-transparent',
  draft:     'border-orange-400 text-orange-500 bg-transparent',
  archived:  'border-gray-400 text-gray-500 bg-transparent',
};

const DIFFICULTY_COLORS: Record<string, string> = {
  easy:   'text-emerald-600',
  medium: 'text-yellow-600',
  hard:   'text-red-600',
};

interface Props {
  /**
   * Navigation is injected rather than derived from a base path because
   * TanStack's `to` is a typed literal union — a computed string would not
   * type-check. Each route passes its own typed navigate call.
   */
  onOpenProblem: (id: string) => void;
  onNewProblem: () => void;
  /** Copy for the count line. The bank a trainer sees is only their own. */
  subtitle?: (total: number) => string;
  /** Shown when the list is empty and no filters are set. */
  emptyDescription?: string;
}

/**
 * The problem bank list, shared by /admin/problems (super_admin, sees every
 * problem) and /trainer/problems (trainer, sees only their own). The two differ
 * by nothing but navigation and copy — the API scopes the rows server-side, so
 * this component never needs to know which caller it is serving.
 */
export function ProblemListPage({
  onOpenProblem,
  onNewProblem,
  subtitle = total => `${total.toLocaleString()} problems in bank`,
  emptyDescription = 'Try adjusting your filters or add a new problem.',
}: Props) {
  const [problems, setProblems] = useState<AdminProblem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const limit = 50;
  const totalPages = Math.ceil(total / limit);

  async function load(p = page) {
    try {
      setLoading(true);
      const data = await adminProblemsApi.list({
        status: statusFilter || undefined,
        difficulty: difficultyFilter || undefined,
        tag: tagFilter || undefined,
        q: search || undefined,
        page: p,
      });
      setProblems(data.problems ?? []);
      setTotal(data.total ?? 0);
    } catch {
      toast.error('Failed to load problems');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { setPage(1); }, [statusFilter, difficultyFilter, tagFilter, search]);
  useEffect(() => { void load(page); }, [statusFilter, difficultyFilter, tagFilter, search, page]);

  async function handleDelete() {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await adminProblemsApi.delete(deleteId);
      toast.success('Problem deleted');
      setDeleteId(null);
      void load();
    } catch {
      toast.error('Failed to delete problem');
    } finally {
      setDeleting(false);
    }
  }

  const activeTopicLabel = TOPICS.find(t => t.slug === tagFilter)?.label;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Problems</h1>
          <p className="text-[13px] text-muted-foreground">
            {loading ? 'Loading…' : subtitle(total)}
          </p>
        </div>
        <Button
          size="sm"
          className={cn('gap-1.5 text-[13px]', PRIMARY_ACTION)}
          onClick={onNewProblem}
        >
          <Plus className="h-3.5 w-3.5" />
          New Problem
        </Button>
      </div>

      {/* Topic pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setTagFilter('')}
          className={cn(
            'text-[12px] px-3 py-1 rounded-full border transition-colors',
            tagFilter === ''
              ? 'bg-foreground text-background border-foreground'
              : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground',
          )}
        >
          All Topics
        </button>
        {TOPICS.map(t => (
          <button
            key={t.slug}
            onClick={() => setTagFilter(t.slug === tagFilter ? '' : t.slug)}
            className={cn(
              'text-[12px] px-3 py-1 rounded-full border transition-colors',
              tagFilter === t.slug
                ? 'bg-foreground text-background border-foreground'
                : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="pl-8 h-9 text-[13px]"
            placeholder="Search problems…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Status filter */}
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 text-[13px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        {/* Difficulty filter */}
        <Select value={difficultyFilter || 'all'} onValueChange={v => setDifficultyFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 text-[13px]">
            <SelectValue placeholder="Difficulty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="easy">Easy</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="hard">Hard</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active filter badges */}
      {(activeTopicLabel || statusFilter || difficultyFilter) && (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground -mt-2">
          Filtering by:
          {activeTopicLabel && (
            <span className="bg-primary/10 text-primary px-2 py-0.5 rounded font-medium flex items-center gap-1">
              {activeTopicLabel}
              <button onClick={() => setTagFilter('')}><X className="h-3 w-3" /></button>
            </span>
          )}
          {statusFilter && (
            <span className="bg-primary/10 text-primary px-2 py-0.5 rounded font-medium capitalize flex items-center gap-1">
              {statusFilter}
              <button onClick={() => setStatusFilter('')}><X className="h-3 w-3" /></button>
            </span>
          )}
          {difficultyFilter && (
            <span className="bg-primary/10 text-primary px-2 py-0.5 rounded font-medium capitalize flex items-center gap-1">
              {difficultyFilter}
              <button onClick={() => setDifficultyFilter('')}><X className="h-3 w-3" /></button>
            </span>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-[11px]">#</TableHead>
              <TableHead className="text-[11px]">Title</TableHead>
              <TableHead className="text-[11px]">Topics</TableHead>
              <TableHead className="text-[11px]">Difficulty</TableHead>
              <TableHead className="text-[11px]">Acceptance</TableHead>
              <TableHead className="text-[11px]">Status</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : problems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState
                    icon={Code2}
                    title="No problems found"
                    description={emptyDescription}
                    className="py-12"
                  />
                </TableCell>
              </TableRow>
            ) : (
              problems.map((p, i) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => onOpenProblem(p.id)}
                >
                  <TableCell className="text-[12px] text-muted-foreground tabular-nums">
                    {(page - 1) * limit + i + 1}
                  </TableCell>
                  <TableCell className="text-[13px] font-medium text-foreground max-w-[240px] truncate">
                    {p.title}
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    <div className="flex flex-wrap gap-1">
                      {(p.tags ?? []).slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className="text-[11px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded capitalize"
                        >
                          {tag.replace(/-/g, ' ')}
                        </span>
                      ))}
                      {(p.tags ?? []).length > 3 && (
                        <span className="text-[11px] text-muted-foreground">+{p.tags.length - 3}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={cn('text-[12px] font-medium capitalize', DIFFICULTY_COLORS[p.difficulty])}>
                      {p.difficulty}
                    </span>
                  </TableCell>
                  <TableCell className="text-[12px] text-muted-foreground tabular-nums">
                    {p.acceptance_rate != null ? `${Number(p.acceptance_rate).toFixed(1)}%` : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge className={cn('text-[11px] border', STATUS_COLORS[p.status])}>
                      {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onOpenProblem(p.id)}>Edit</DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleteId(p.id)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-[13px] px-3 py-1 border border-border rounded-md bg-background">
              {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!deleteId} onOpenChange={open => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete problem?</DialogTitle>
            <DialogDescription>
              This permanently deletes the problem and all its test cases. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
